import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { TrendingUp, Users, Building2, ArrowRight, ArrowLeft } from "lucide-react";
import { AdvisorCredentialsSelect } from "@/components/onboarding/AdvisorCredentialsSelect";
import { CountryCurrencyPicker } from "@/components/onboarding/CountryCurrencyPicker";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/services/LoggingService";
import { getDefaultCurrencyForCountry } from "@/constants/countries";

type Persona = "investor" | "advisor" | "asset_manager";

interface WelcomeIntroDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (answers: {
    persona?: Persona;
    name?: string;
    country?: string;
    citizenship?: string;
    default_currency?: string;
    goals?: string[];
    goals_other?: string;
    investment_styles?: string[];
    seeking_clients?: string;
    aum?: string;
    advisor_credentials?: string[];
    referral?: string;
    referral_source?: string;
    linkedin_profile?: string;
  }) => Promise<void> | void;
  onSkip: () => Promise<void> | void;
}

export function WelcomeIntroDialog({ open, onOpenChange, onSubmit, onSkip }: WelcomeIntroDialogProps) {
  const [step, setStep] = useState(1);
  const [isClosing, setIsClosing] = useState(false);
  const [persona, setPersona] = useState<Persona | undefined>();
  const [name, setName] = useState("");
  const [country, setCountry] = useState<string | undefined>(undefined);
  const [defaultCurrency, setDefaultCurrency] = useState<string | undefined>(undefined);
  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);
  const [investmentGoals, setInvestmentGoals] = useState<string[]>([]);
  const [investmentGoalsOther, setInvestmentGoalsOther] = useState("");
  const [seekingClients, setSeekingClients] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<string[]>([]);
  const [aum, setAum] = useState<string | null>(null);
  const [referralSource, setReferralSource] = useState<string | null>(null);

  // Prefill from user profile or waitlist before geolocation
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: sess } = await supabase.auth.getSession();
        const uid = sess?.session?.user?.id;
        const email = sess?.session?.user?.email;
        if (uid) {
          const { data: prof } = await supabase
            .from('user_profiles')
            .select('first_name,last_name,country,default_currency,persona')
            .eq('id', uid)
            .maybeSingle();
          if (!cancelled && prof) {
            if (!name && (((prof as any).first_name) || ((prof as any).last_name))) {
              const first = ((prof as any).first_name || '').trim();
              const last = ((prof as any).last_name || '').trim();
              const full = [first, last].filter(Boolean).join(' ');
              if (full) setName(full);
            }
            if (!country && (prof as any).country) setCountry((prof as any).country);
            if (!defaultCurrency && (prof as any).default_currency) setDefaultCurrency(String((prof as any).default_currency).toUpperCase());
            if (!persona && (prof as any).persona) setPersona((prof as any).persona);
          }
        } else if (email) {
          const { data: wl } = await supabase
            .from('user_waitlist')
            .select('full_name,country,default_currency,persona')
            .eq('email', email)
            .maybeSingle();
          if (!cancelled && wl) {
            if (!name && (wl as any).full_name) setName(String((wl as any).full_name));
            if (!country && (wl as any).country) setCountry((wl as any).country);
            if (!defaultCurrency && (wl as any).default_currency) setDefaultCurrency(String((wl as any).default_currency).toUpperCase());
            if (!persona && (wl as any).persona) setPersona((wl as any).persona);
          }
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [open]);

  // On open, clear any stale geo cache key so dev tools don't show confusing values
  useEffect(() => {
    if (!open) return;
    try { localStorage.removeItem('alts__geo_country'); } catch {}
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // No cache read/write: only log live lookups below

    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('geo-country');
        if (!error && data && data.country && !cancelled) {
          const code = String(data.country).toUpperCase();
          logger.info("welcome.geo", "ip-detect", { source: "edge", code });
          return;
        }
      } catch {
        // ignore and try client fallback
      }
      // Client-side fallback if Edge Function not available or unauthenticated
      try {
        const r = await fetch('https://ipapi.co/json/', { headers: { 'Accept': 'application/json' } });
        if (!r.ok) return;
        const j = await r.json();
        const code = typeof j?.country === 'string' ? j.country.toUpperCase() : undefined;
        if (!code) return;
        logger.info("welcome.geo", "ip-detect", { source: "client_fallback", code });
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [open]);

  const totalSteps = useMemo(() => {
    if (persona === 'investor') return 5;
    if (persona === 'advisor') return 5;
    if (persona === 'asset_manager') return 4;
    return 4;
  }, [persona]);

  const onCountryPicked = (val: string) => {
    setCountry(val);
    // Currency auto-selection handled by CountryCurrencyPicker effect; preserve manual user selection
  };

  const investStyleOptions = [
    { id: 'active', label: 'Actively manage my portfolio.', sublabel: '(e.g. Buy individual stocks and bonds)' },
    { id: 'passive', label: 'Passively manage my portfolio.', sublabel: '(e.g. Invest in ETFs or mutual funds)' },
    { id: 'advisor', label: 'Partner with a financial advisor' }
  ];

  const goals = [
    { id: 'diversify', label: 'Diversify my portfolio' },
    { id: 'income', label: 'Generate income' },
    { id: 'wealth', label: 'Grow long term wealth' },
    { id: 'volatility', label: 'Reduce portfolio volatility' },
    { id: 'other', label: 'Other' }
  ];

  const roles = [
    { id: 'investor', title: 'I invest for myself', subtitle: 'Individual Investor', description: 'See, plan and build investment portfolios', benefits: ['Unified assets', 'Portfolio assessment', 'Diversification planning'], icon: TrendingUp },
    { id: 'advisor', title: 'I advise clients', subtitle: 'Financial Advisor', description: 'Powerful tools for building client portfolios', benefits: ['Portfolio construction', 'Market data', 'Client collaboration'], icon: Users },
    { id: 'asset_manager', title: 'I manage assets', subtitle: 'Asset Manager', description: 'Fund, family office, investment dealer, institution', benefits: ['AI-powered tools', 'Alternative investments', 'Advanced analytics'], icon: Building2 }
  ] as const;

  const aumRanges = [
    { id: 'under-50m', label: '< $50M' },
    { id: '50m-100m', label: '$50M - 100M' },
    { id: '100m-250m', label: '$100M - 250M' },
    { id: '250m-500m', label: '$250M - 500M' },
    { id: '500m-1b', label: '$500 - $1B' },
    { id: '1b-10b', label: '$1B - $10B' },
    { id: '10b-plus', label: '$10B +' }
  ];

  const referralSources = [
    { id: 'friends', label: 'Friends' },
    { id: 'linkedin', label: 'LinkedIn' },
    { id: 'twitter', label: 'X / Twitter' },
    { id: 'tiktok', label: 'TikTok' },
    { id: 'newsletter', label: 'Newsletter' },
    { id: 'media', label: 'TV / Newspaper' }
  ];

  const toggleGoal = (id: string) => {
    setInvestmentGoals(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleStyle = (id: string) => {
    setSelectedStyles(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const canContinueStep2 = !!(persona && name.trim() && country && defaultCurrency);

  const handleClose = () => {
    setIsClosing(true);
    onOpenChange(false);
  };

  const handleFinalSubmit = async () => {
    if (!persona) return;
    const countryName = country;
    const finalCurrency = defaultCurrency || getDefaultCurrencyForCountry(countryName || undefined);
    await onSubmit({
      persona,
      name: name.trim(),
      country: countryName,
      citizenship: countryName,
      default_currency: finalCurrency,
      goals: persona === 'investor' ? investmentGoals : undefined,
      goals_other: persona === 'investor' && investmentGoals.includes('other') ? investmentGoalsOther || undefined : undefined,
      investment_styles: persona === 'investor' ? (selectedStyles.length ? selectedStyles : undefined) : undefined,
      seeking_clients: persona === 'advisor' ? seekingClients || undefined : undefined,
      aum: persona === 'asset_manager' ? aum || undefined : undefined,
      advisor_credentials: persona === 'advisor' ? credentials : undefined,
      referral_source: referralSource || undefined,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 w-[92vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl">
        <div>
          <div className="p-4 md:px-6 md:py-6 bg-white">
              <div className="flex items-center gap-1.5 mb-6">
                {Array.from({ length: totalSteps }).map((_, idx) => {
                  const s = idx + 1;
                  return (
                    <div key={s} className={`h-1.5 rounded-full transition-all duration-300 ${s === step ? 'w-8 bg-gray-900' : s < step ? 'w-6 bg-gray-400' : 'w-4 bg-gray-200'}`} />
                  );
                })}
              </div>
              {/* Step 1: Role selection */}
              {step === 1 && (
                <div>
                  <div className="mb-6 text-center">
                    <h2 className="text-balance text-xl md:text-2xl font-semibold tracking-tight leading-snug text-foreground">
                      New here? Let's personalize your experience...
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">Choose the option that best describes you.</p>
                  </div>
                  <div className="space-y-3 mb-6">
                    {roles.map((r) => {
                      const Icon = r.icon; return (
                        <button key={r.id} onClick={() => { setPersona(r.id as Persona); setTimeout(() => setStep(2), 200); }}
                          className="w-full p-4 rounded-2xl border-2 border-gray-200 bg-white hover:border-gray-400 hover:shadow-md transition-all text-left group relative overflow-hidden">
                          <div className="absolute inset-0 bg-gray-100 opacity-0 group-hover:opacity-30 transition-opacity" />
                          <div className="relative flex items-start gap-4">
                            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gray-900 flex items-center justify-center shadow-md">
                              <Icon className="w-7 h-7 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-1.5">
                                <div>
                                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-0.5">{r.title}</h3>
                                  <p className="text-xs md:text-sm font-medium text-gray-500">{r.subtitle}</p>
                                </div>
                                <ArrowRight className="flex-shrink-0 w-5 h-5 ml-4 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-1 transition-all" />
                              </div>
                              <p className="text-gray-600 mb-2 leading-relaxed text-sm">{r.description}</p>
                              <div className="flex flex-wrap gap-1.5">
                                {r.benefits.map((b, i) => (
                                  <span key={i} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700">{b}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="pt-3 border-t text-center">
                    <button onClick={() => { setIsClosing(true); onSkip(); onOpenChange(false); }} className="text-xs md:text-sm text-gray-500 hover:text-gray-700 font-medium">I'm just looking around</button>
                  </div>
                </div>
              )}

              {/* Step 2: Name, Country, Currency */
              }
              {step === 2 && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1.5">Welcome aboard!</h2>
                    <p className="text-xs md:text-sm text-gray-600">Tell us a bit about yourself</p>
                  </div>
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">What's your name?</label>
                      <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter your name" className="w-full px-3 py-2 rounded-xl border-2 border-gray-200 focus:border-gray-900 focus:outline-none text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">Country & currency</label>
                      <CountryCurrencyPicker
                        country={country}
                        currency={defaultCurrency}
                        onCountryChange={onCountryPicked}
                        onCurrencyChange={setDefaultCurrency}
                      />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setStep(1)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-700 hover:bg-gray-50 font-medium text-sm">
                      <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                    <button onClick={() => canContinueStep2 && setStep(3)} disabled={!canContinueStep2} className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gray-900 text-white hover:bg-gray-800 disabled:bg-gray-300 font-medium text-sm">
                      Continue <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3 variants */}
              {step === 3 && persona === 'investor' && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1.5">How do you typically invest?</h2>
                    <p className="text-xs md:text-sm text-gray-600">Select all that apply.</p>
                  </div>
                  <div className="space-y-2.5 mb-6">
                    {investStyleOptions.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => toggleStyle(s.id)}
                        className={`w-full p-3.5 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${selectedStyles.includes(s.id) ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`}
                      >
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedStyles.includes(s.id) ? 'border-gray-900' : 'border-gray-300'}`}>
                          {selectedStyles.includes(s.id) && (<div className="w-2.5 h-2.5 rounded-full bg-gray-900" />)}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 text-sm md:text-base">{s.label}</div>
                          {s.sublabel && (<div className="text-xs md:text-sm text-gray-600">{s.sublabel}</div>)}
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setStep(2)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-700 hover:bg-gray-50 font-medium text-sm"><ArrowLeft className="w-4 h-4" /> Back</button>
                    <button onClick={() => setStep(4)} className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gray-900 text-white hover:bg-gray-800 font-medium text-sm">Continue <ArrowRight className="w-4 h-4" /></button>
                  </div>
                </div>
              )}

              {step === 3 && persona === 'advisor' && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1.5">Looking for opportunities to work with new clients?</h2>
                    <p className="text-xs md:text-sm text-gray-600">Select one.</p>
                  </div>
                  <div className="space-y-2.5 mb-6">
                    {[{id:'yes',label:"Yes, I'm open to new clients"},{id:'no',label:'No, not at this time'},{id:'maybe',label:'Maybe, depending on fit'}].map(o => (
                      <button key={o.id} onClick={() => setSeekingClients(o.id)} className={`w-full p-3.5 rounded-xl border-2 transition-all text-left ${seekingClients === o.id ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <div className="font-medium text-gray-900 text-sm md:text-base">{o.label}</div>
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setStep(2)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-700 hover:bg-gray-50 font-medium text-sm"><ArrowLeft className="w-4 h-4" /> Back</button>
                    <button onClick={() => setStep(4)} className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gray-900 text-white hover:bg-gray-800 font-medium text-sm">Continue <ArrowRight className="w-4 h-4" /></button>
                  </div>
                </div>
              )}

              {step === 3 && persona === 'asset_manager' && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1.5">What is your total AUM?</h2>
                    <p className="text-xs md:text-sm text-gray-600">Select one.</p>
                  </div>
                  <div className="flex flex-wrap gap-2.5 mb-6">
                    {aumRanges.map(r => (
                      <button key={r.id} onClick={() => setAum(r.id)} className={`inline-flex items-center px-4 py-2.5 rounded-xl border-2 transition-all ${aum === r.id ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <span className="font-medium text-gray-900 text-sm md:text-base">{r.label}</span>
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setStep(2)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-700 hover:bg-gray-50 font-medium text-sm"><ArrowLeft className="w-4 h-4" /> Back</button>
                    <button onClick={() => setStep(4)} disabled={!aum} className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gray-900 text-white hover:bg-gray-800 disabled:bg-gray-300 font-medium text-sm">Continue <ArrowRight className="w-4 h-4" /></button>
                  </div>
                </div>
              )}

              {/* Step 4 variants */}
              {step === 4 && persona === 'investor' && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1.5">What are your investment priorities?</h2>
                    <p className="text-xs md:text-sm text-gray-600">Select all that apply.</p>
                  </div>
                  <div className="flex flex-wrap gap-2.5 mb-5">
                    {goals.map(g => (
                      <button key={g.id} onClick={() => toggleGoal(g.id)} className={`inline-flex items-center gap-3 px-4 py-2.5 rounded-xl border-2 transition-all ${investmentGoals.includes(g.id) ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <span className="font-medium text-gray-900 text-sm md:text-base">{g.label}</span>
                      </button>
                    ))}
                  </div>
                  {investmentGoals.includes('other') && (
                    <div className="mb-6">
                      <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5">Please specify</label>
                      <input type="text" value={investmentGoalsOther} onChange={(e) => setInvestmentGoalsOther(e.target.value)} placeholder="Enter your investment goal" className="w-full px-3 py-2 rounded-xl border-2 border-gray-200 focus:border-gray-900 focus:outline-none text-sm" />
                    </div>
                  )}
                  <div className="flex gap-3">
                    <button onClick={() => setStep(3)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-700 hover:bg-gray-50 font-medium text-sm"><ArrowLeft className="w-4 h-4" /> Back</button>
                    <button onClick={() => setStep(5)} className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gray-900 text-white hover:bg-gray-800 font-medium text-sm">Continue <ArrowRight className="w-4 h-4" /></button>
                  </div>
                </div>
              )}

              {step === 4 && persona === 'advisor' && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1.5">Certifications</h2>
                    <p className="text-xs md:text-sm text-gray-600">Select all that apply.</p>
                  </div>
                  <div className="mb-6">
                    <AdvisorCredentialsSelect country={country} value={credentials} onChange={setCredentials} />
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setStep(3)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-700 hover:bg-gray-50 font-medium text-sm"><ArrowLeft className="w-4 h-4" /> Back</button>
                    <button onClick={() => setStep(5)} className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gray-900 text-white hover:bg-gray-800 font-medium text-sm">Continue <ArrowRight className="w-4 h-4" /></button>
                  </div>
                </div>
              )}

              {/* Final step: referral */}
              {((step === 5 && (persona === 'investor' || persona === 'advisor')) || (step === 4 && persona === 'asset_manager')) && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1.5">How did you hear about us?</h2>
                    <p className="text-xs md:text-sm text-gray-600">Select one.</p>
                  </div>
                  <div className="flex flex-wrap gap-2.5 mb-6">
                    {referralSources.map(s => (
                      <button key={s.id} onClick={() => setReferralSource(s.id)} className={`inline-flex items-center gap-3 px-4 py-2.5 rounded-xl border-2 transition-all ${referralSource === s.id ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <span className="font-medium text-gray-900 text-sm md:text-base">{s.label}</span>
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setStep(step - 1)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-700 hover:bg-gray-50 font-medium text-sm"><ArrowLeft className="w-4 h-4" /> Back</button>
                    <button onClick={handleFinalSubmit} disabled={!referralSource} className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gray-900 text-white hover:bg-gray-800 disabled:bg-gray-300 font-medium text-sm">Get Started <ArrowRight className="w-4 h-4" /></button>
                  </div>
                </div>
              )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
